import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, of, shareReplay } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BranchMapping } from '../../features/incidents/models/teradata-rpp';
import { BranchDetail } from '../auth/models/user-info';
import { EnvironmentService } from '../environment';
import { TASK_LIST_READ_ONLY } from './task-list-stale.interceptor';

@Injectable({
  providedIn: 'root'
})
export class BranchMappingService {
  private readonly httpClient = inject(HttpClient);
  private readonly environmentService = inject(EnvironmentService);

  private branchMappingsSubject = new BehaviorSubject<BranchMapping[]>([]);
  private branchMappings$ = this.branchMappingsSubject.asObservable();

  // Lookup index for getBranchName, rebuilt when the mappings are replaced.
  private nameByBranchNumber = new Map<string, string>();
  private indexedMappings: BranchMapping[] | null = null;

  /**
   * Get all branch mappings from batch API (primary method)
   * Calls POST /v1/damage-branches/batch via userInfo service to fetch all available branches
   * branch_name already contains formatted "code - name" string
   */
  getBranchMappings(): Observable<BranchMapping[]> {
    if (this.branchMappingsSubject.value.length > 0) {
      return this.branchMappings$;
    }

    // Fetch all branches from the batch endpoint
    return this.httpClient
      .post<BranchMapping[]>(
        `${this.environmentService.apiUrls.userInfo}/v1/damage-branches/batch`,
        { branch_numbers: [] },  // Empty array fetches all available branches
        { context: new HttpContext().set(TASK_LIST_READ_ONLY, true) }
      )
      .pipe(
        map(response => {
          if (Array.isArray(response)) {
            // Cache the response
            this.branchMappingsSubject.next(response);
            return response;
          }
          return [];
        }),
        shareReplay(1)
      );
  }

  /**
   * Get branch mappings from batch API response
   * Uses POST endpoint via userInfo service to fetch all available branch mappings
   * Returns array response: [{ branch_number, branch_name, source }, ...]
   */
  getBranchMappingsBatch(): Observable<BranchMapping[]> {
    if (this.branchMappingsSubject.value.length > 0) {
      return this.branchMappings$;
    }

    return this.httpClient
      .post<BranchMapping[]>(
        `${this.environmentService.apiUrls.userInfo}/v1/damage-branches/batch`,
        { branch_numbers: [] },  // Empty array fetches all available branches
        { context: new HttpContext().set(TASK_LIST_READ_ONLY, true) }
      )
      .pipe(
        map(response => {
          if (Array.isArray(response)) {
            this.branchMappingsSubject.next(response);
            return response;
          }
          return [];
        }),
        shareReplay(1)
      );
  }

  /**
   * Initialize branch mappings on app startup
   * Fetches all available branch mappings via batch API and caches them
   */
  initializeBranchMappings(): Observable<BranchMapping[]> {
    return this.getBranchMappings();
  }

  /**
   * Initialize branch mappings for specific user branches
   * Fetches branch mappings for the user's assigned branches
   */
  initializeBranchMappingsForUser(branchNumbers: string[]): Observable<BranchMapping[]> {
    if (!branchNumbers || branchNumbers.length === 0) {
      // If no branches provided, get all branches
      return this.initializeBranchMappings();
    }

    const payload = { branch_numbers: branchNumbers };
    return this.httpClient
      .post<BranchMapping[]>(
        `${this.environmentService.apiUrls.userInfo}/v1/damage-branches/batch`,
        payload
      )
      .pipe(
        map(response => {
          if (Array.isArray(response)) {
            // Cache the response
            this.branchMappingsSubject.next(response);
            return response;
          }
          return [];
        }),
        shareReplay(1)
      );
  }

  /**
   * Get branch name directly by branch number
   * Returns formatted string like "9738 - NORTH SAN JOSE CA"
   * Falls back to branch number if branch not found or name is empty
   */
  getBranchName(branchNumber: string): string {
    const name = this.branchNameIndex().get(branchNumber);
    return name?.trim() ? name : branchNumber;
  }

  private branchNameIndex(): Map<string, string> {
    const current = this.branchMappingsSubject.value;
    if (this.indexedMappings !== current) {
      this.nameByBranchNumber = new Map(
        current.map(branch => [branch.branch_number, branch.branch_name]),
      );
      this.indexedMappings = current;
    }
    return this.nameByBranchNumber;
  }

  /**
   * Get branch names for multiple branch numbers
   * Returns array of formatted branch names
   */
  getBranchNamesForCodes(branchNumbers: string[]): string[] {
    return branchNumbers.map(code => this.getBranchName(code));
  }

  /**
   * Update the cached branch mappings
   */
  setBranchMappings(mappings: BranchMapping[]): void {
    this.branchMappingsSubject.next(mappings);
  }

  /**
   * Reset cache (useful for refresh scenarios)
   */
  resetCache(): void {
    this.branchMappingsSubject.next([]);
  }

  private allBranchDetailsCache: BranchDetail[] | null = null;
  private pendingAllBranchDetails$: Observable<BranchDetail[]> | null = null;

  getAllBranchDetails(): Observable<BranchDetail[]> {
    if (this.allBranchDetailsCache !== null) {
      return of(this.allBranchDetailsCache);
    }
    if (this.pendingAllBranchDetails$) {
      return this.pendingAllBranchDetails$;
    }
    this.pendingAllBranchDetails$ = this.httpClient
      .get<BranchDetail[]>(`${this.environmentService.apiUrls.userInfo}/v1/user/branch-details`)
      .pipe(
        tap(details => {
          this.allBranchDetailsCache = details;
          this.pendingAllBranchDetails$ = null;
        }),
        shareReplay(1)
      );
    return this.pendingAllBranchDetails$;
  }

  /**
   * Get branch options formatted for dropdowns/selects
   * Uses cached branch mappings from batch API
   * Returns array with branch_number and branch_name
   * Falls back to branch_number if branch_name is empty
   */
  getBranchOptions(): Observable<{ value: string; label: string }[]> {
    return this.getBranchMappings().pipe(
      map(branches => {
        const options = branches.map(branch => ({
          value: branch.branch_number,
          label: branch.branch_name?.trim() ? branch.branch_name : branch.branch_number
        }));
        return options;
      })
    );
  }

  /**
   * Get branch options from batch API response directly
   * Simplified for direct array responses
   * Falls back to branch_number if branch_name is empty
   */
  getBranchOptionsBatch(): Observable<{ value: string; label: string }[]> {
    return new Observable(observer => {
      this.getBranchMappingsBatch().subscribe(
        branches => {
          const options = branches.map(branch => ({
            value: branch.branch_number,
            label: branch.branch_name?.trim() ? branch.branch_name : branch.branch_number
          }));
          observer.next(options);
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }

  /**
   * POST batch request to get branch mappings for specific branch numbers
   * Calls userInfo service endpoint
   * Payload: { branch_numbers: ["9251", "9378", "9999"] }
   * Returns: [{ branch_number, branch_name }, ...]
   */
  getBranchMappingsForNumbers(branchNumbers: string[]): Observable<BranchMapping[]> {
    const payload = { branch_numbers: branchNumbers };
    const batchUrl = `${this.environmentService.apiUrls.userInfo}/v1/damage-branches/batch`;
    return this.httpClient
      .post<BranchMapping[]>(batchUrl, payload)
      .pipe(shareReplay(1));
  }

  /**
   * Get branch options for specific branch numbers
   * POST batch request and format as dropdown options
   * Falls back to branch_number if branch_name is empty
   */
  getBranchOptionsForNumbers(branchNumbers: string[]): Observable<{ value: string; label: string }[]> {
    return new Observable(observer => {
      this.getBranchMappingsForNumbers(branchNumbers).subscribe(
        branches => {
          const options = branches.map(branch => ({
            value: branch.branch_number,
            label: branch.branch_name?.trim() ? branch.branch_name : branch.branch_number
          }));
          observer.next(options);
          observer.complete();
        },
        error => observer.error(error)
      );
    });
  }
}
